/**
 * Thin fetch wrapper used everywhere in the SPA.
 *
 * Sets `credentials: 'include'` (so rs_session HttpOnly cookie rides
 * along on cookie-auth routes), attaches `Authorization: Bearer
 * <jwt>` if a wallet JWT is in sessionStorage, and on 401 redirects
 * to /signin with a reason marker. Throws a typed `ApiError` on
 * non-2xx so callers can `try/catch` and surface the error code.
 */

const WALLET_JWT_KEY = 'vfWalletJwt';

export interface WalletJwtRecord {
  token: string;
  expiresAt: number; // unix seconds
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'ApiError';
  }
}

function readWalletJwt(): string | null {
  try {
    const raw = sessionStorage.getItem(WALLET_JWT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WalletJwtRecord;
    if (typeof parsed?.token !== 'string') return null;
    if (typeof parsed?.expiresAt !== 'number') return null;
    if (parsed.expiresAt <= Math.floor(Date.now() / 1000)) {
      sessionStorage.removeItem(WALLET_JWT_KEY);
      return null;
    }
    return parsed.token;
  } catch {
    return null;
  }
}

export interface FetcherOptions extends RequestInit {
  /** If true, skip the auto-redirect-to-signin on 401. */
  noAuthRedirect?: boolean;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: FetcherOptions = {},
): Promise<T> {
  const { noAuthRedirect, headers, ...rest } = options;
  const merged: Record<string, string> = {
    Accept: 'application/json',
    ...(headers as Record<string, string> | undefined),
  };
  if (rest.body && !merged['Content-Type']) {
    merged['Content-Type'] = 'application/json';
  }
  const jwt = readWalletJwt();
  if (jwt) merged.Authorization = `Bearer ${jwt}`;

  const res = await fetch(path, {
    credentials: 'include',
    ...rest,
    headers: merged,
  });

  if (res.status === 401 && !noAuthRedirect) {
    if (typeof window !== 'undefined') {
      window.location.replace('/signin?reason=session_expired');
    }
    throw new ApiError(401, 'session_expired');
  }

  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // non-JSON body — leave as raw text on the error
    }
  }

  if (!res.ok) {
    const code =
      (body && typeof body === 'object' && 'code' in body
        ? String((body as { code: unknown }).code)
        : null) ?? `http_${res.status}`;
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : text || code;
    throw new ApiError(res.status, code, message);
  }

  return body as T;
}

export const walletJwtStorage = {
  read: readWalletJwt,
  write(token: string, expiresAt: number): void {
    sessionStorage.setItem(
      WALLET_JWT_KEY,
      JSON.stringify({ token, expiresAt } satisfies WalletJwtRecord),
    );
  },
  clear(): void {
    sessionStorage.removeItem(WALLET_JWT_KEY);
  },
};
